/**
 * Footsteps: nine surfaces, each a row of numbers - the band of noise a boot makes on it, the
 * heel's thud, and whatever is peculiar to it (mud sucks, water bubbles, ice clicks). From a row
 * come two patches: `step.*` (walk; a run plays it louder and brighter) and `crawl.*`, the same
 * texture stretched into a drag. With the screen off, the surface must still be obvious (07 §5).
 */

import type { Surface } from '../surface';
import { SURFACES } from '../surface';
import type { FilterSpec, Layer, Patch } from '../synth/patch';
import { PRIORITY, type SfxDef } from './types';

interface Ground {
  wave: 'noise' | 'pink';
  filter: FilterSpec;
  attack: number;
  decay: number;
  /** Granular surfaces (snow, gravel) crunch: a fast tremolo on the texture. */
  grain?: [hz: number, depth: number];
  /** Heel thud: start Hz, end Hz, level. Soft ground has almost none. */
  thud: [freq: number, freqEnd: number, gain: number];
  extra?: Layer[];
  /** Mix level of a walking step on this surface. */
  level: number;
}

const GROUND: Record<Surface, Ground> = {
  snow: {
    wave: 'noise', filter: { type: 'bp', cutoff: 2600, cutoffEnd: 1300, q: 1.6 }, attack: 0.008, decay: 0.14,
    grain: [68, 0.65], thud: [92, 58, 0.3], level: 0.3,
  },
  ice: {
    wave: 'noise', filter: { type: 'bp', cutoff: 2600, q: 3 }, attack: 0.002, decay: 0.06,
    thud: [150, 110, 0.15], level: 0.28,
    extra: [
      { wave: 'sine', freq: 1900, fmRatio: 2.7, fmIndex: 1.8, fmDecay: 0.01, decay: 0.035, curve: 3, gain: 0.6 },
      { wave: 'noise', decay: 0.022, curve: 3, gain: 0.7, filter: { type: 'hp', cutoff: 3600 } },
    ],
  },
  sand: {
    wave: 'noise', filter: { type: 'bp', cutoff: 2100, cutoffEnd: 1000, q: 0.8 }, attack: 0.02, decay: 0.17,
    thud: [80, 55, 0.12], level: 0.24,
  },
  rock: {
    wave: 'noise', filter: { type: 'bp', cutoff: 3300, cutoffEnd: 1800, q: 1.5 }, attack: 0.003, decay: 0.1,
    grain: [96, 0.8], thud: [120, 75, 0.4], level: 0.3,
    extra: [{ wave: 'noise', decay: 0.015, curve: 3, gain: 0.6, filter: { type: 'hp', cutoff: 5000 } }],
  },
  grass: {
    wave: 'pink', filter: { type: 'bp', cutoff: 1400, cutoffEnd: 700, q: 0.9 }, attack: 0.015, decay: 0.17,
    thud: [84, 55, 0.35], level: 0.24,
  },
  mud: {
    wave: 'noise', filter: { type: 'lp', cutoff: 950, cutoffEnd: 300 }, attack: 0.015, decay: 0.17,
    thud: [90, 50, 0.4], level: 0.3,
    extra: [{ wave: 'sine', at: 0.05, freq: 140, freqEnd: 270, attack: 0.01, decay: 0.08, gain: 0.45 }],
  },
  water: {
    wave: 'noise', filter: { type: 'bp', cutoff: 1050, cutoffEnd: 480, q: 1 }, attack: 0.02, decay: 0.27,
    thud: [70, 45, 0.2], level: 0.34,
    extra: [
      { wave: 'sine', at: 0.06, freq: 520, freqEnd: 1150, attack: 0.005, decay: 0.05, gain: 0.3 },
      { wave: 'sine', at: 0.13, freq: 700, freqEnd: 1500, attack: 0.005, decay: 0.04, gain: 0.2 },
    ],
  },
  wood: {
    wave: 'noise', filter: { type: 'bp', cutoff: 420, q: 5 }, attack: 0.002, decay: 0.07,
    thud: [175, 118, 0.9], level: 0.32,
    extra: [{ wave: 'noise', decay: 0.012, curve: 3, gain: 0.4, filter: { type: 'hp', cutoff: 2500 } }],
  },
  concrete: {
    wave: 'noise', filter: { type: 'bp', cutoff: 1900, q: 2 }, attack: 0.002, decay: 0.05,
    thud: [125, 80, 0.8], level: 0.28,
    extra: [{ wave: 'noise', decay: 0.015, curve: 3, gain: 0.55, filter: { type: 'hp', cutoff: 2200 } }],
  },
};

function texture(g: Ground, stretch: number, gain: number): Layer {
  const layer: Layer = { wave: g.wave, attack: g.attack * stretch, decay: g.decay * stretch, gain, filter: g.filter };
  if (g.grain) {
    layer.tremHz = g.grain[0] / Math.sqrt(stretch);
    layer.tremDepth = g.grain[1];
  }
  return layer;
}

function stepPatch(g: Ground): Patch {
  const thud: Layer = { wave: 'sine', freq: g.thud[0], freqEnd: g.thud[1], decay: 0.06, gain: g.thud[2] };
  return { layers: [texture(g, 1, 1), thud, ...(g.extra ?? [])], jitter: { pitch: 0.12, time: 0.18, gain: 0.2 } };
}

/** A body dragged along on elbows: the surface's texture, long and soft, and no heel at all. */
function crawlPatch(g: Ground): Patch {
  const drag = texture(g, 3.2, 1);
  drag.attack = Math.max(0.05, drag.attack ?? 0);
  const cloth: Layer = {
    wave: 'pink', attack: 0.06, decay: 0.3, gain: 0.5, filter: { type: 'bp', cutoff: 1500, q: 0.7 },
  };
  return { layers: [drag, cloth], jitter: { pitch: 0.12, time: 0.2, gain: 0.2 } };
}

export type FootstepId = `step.${Surface}` | `crawl.${Surface}`;

function build(): Record<FootstepId, SfxDef> {
  const out: Partial<Record<FootstepId, SfxDef>> = {};
  for (const surface of SURFACES) {
    const g = GROUND[surface];
    out[`step.${surface}`] = {
      patch: stepPatch(g), variants: 4, priority: PRIORITY.detail, gain: g.level, rateSpread: 0.06, minInterval: 0.04,
    };
    out[`crawl.${surface}`] = {
      patch: crawlPatch(g), variants: 3, priority: PRIORITY.ambient, gain: g.level * 0.5,
      rateSpread: 0.08, minInterval: 0.08,
    };
  }
  return out as Record<FootstepId, SfxDef>;
}

export const FOOTSTEP_SFX: Record<FootstepId, SfxDef> = build();

function ids(prefix: 'step' | 'crawl'): Record<Surface, FootstepId> {
  const out: Partial<Record<Surface, FootstepId>> = {};
  for (const surface of SURFACES) out[surface] = `${prefix}.${surface}`;
  return out as Record<Surface, FootstepId>;
}

/** Sound ids by surface, built once so a footfall never concatenates a string. */
export const STEP_ID: Record<Surface, FootstepId> = ids('step');
export const CRAWL_ID: Record<Surface, FootstepId> = ids('crawl');
